<template>
  <div>
    <div class="feedback">
      <transition name="fade" @enter="startAutoHide">
        <feedback-error v-if="isVisible" :message="message" />
      </transition>
    </div>
    <SearchFormHeader
      :filter-count="filterCount"
      :sort-options="itemSortOptions"
      :sort-selected-value="conditions.sortSelectedValue"
      :total-count="pagination.totalCount"
      :is-loading="isLoadingTotalCount"
      @click-filter="isFilterOpen = true"
      @change-sort="onSelectSort"
    />
    <ItemSearchResults
      :items="items"
      :status="status"
      :current-page="pagination.currentPage"
      :total-pages="pagination.totalPages"
      :three-columns="threeColumns"
      @click-item="onClickItem"
      @change-page="onClickPageLink"
    />
    <SearchForm
      :showable-search-form="isFilterOpen"
      :keyword="getCurrentInputtedKeyword()"
      :categories="filterOptions.categories"
      :higher-budget="conditions.higherBudget"
      :lower-budget="conditions.lowerBudget"
      :scenes="filterOptions.scenes"
      :receiving-kinds="filterOptions.receivingKinds"
      :selected-category-ids="conditions.selectedCategoryIds"
      :selected-scene-ids="conditions.selectedSceneIds"
      :selected-receiving-kind-ids="conditions.selectedReceivingKindIds"
      @hide="isFilterOpen = false"
      @submit="onSubmitSearchForm"
    />
    <IndexPageRecentViewSection
      @click-item-box="onClickItemBox"
      @click-section-header-link="onClickSectionHeaderLink"
    />
    <div v-if="showEmptyResultContents" class="search-result--empty">
      <IndexPageSceneListSection class="scene" />
      <IndexPageBrandSection />
      <IndexPageCategoriesSection @error="show" />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { itemSortOptions } from '../../../constants/itemSearchConstatnts.js'
import { useItemSearch } from '../../../features/item-search/useItemSearch.js'
import { useItemSearchOptions } from '../../../features/item-search/useItemSearchOptions.js'
import { useFeedback } from '../../../composables/useFeedback.js'
import { useItemIndexPageAction } from '../../../features/item-search/useItemIndexPageAction.js'
import ItemSearchResults from '../../../features/item-search/ItemSearchResults.vue'
import SearchFormHeader from '../../organism/search_forms/SearchFormHeader.vue'
import SearchForm from '../../organism/search_forms/SearchForm.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import IndexPageRecentViewSection from '../../organism/homes/sections/IndexPageRecentViewSection.vue'
import IndexPageBrandSection from '../../organism/homes/sections/IndexPageBrandSection.vue'
import IndexPageSceneListSection from '../../organism/homes/sections/IndexPageSceneListSection.vue'
import IndexPageCategoriesSection from '../../organism/homes/sections/IndexPageCategoriesSection.vue'
import { trackAbTestImpression } from '../../../utils/ab-test-impression'

const props = defineProps({
  // FIXME(item_list_three_columns): ABテスト決着後、threeColumnsの分岐を削除する
  threeColumns: { type: Boolean, required: false, default: false },
  abTestAssignment: { type: Object, required: false, default: null }
})

const {
  conditions,
  items,
  status,
  pagination,
  error,
  filterCount,
  isLoadingTotalCount,
  searchFromUrl,
  changeSort,
  changeFilters,
  changePage
} = useItemSearch()
const {
  filterOptions,
  error: filterOptionsError,
  loadOptions
} = useItemSearchOptions()
const { isVisible, message, show, startAutoHide } = useFeedback()
const isFilterOpen = ref(false)
const {
  getCurrentInputtedKeyword,
  onPopstate,
  onSelectSort,
  onClickPageLink,
  onSubmitSearchForm,
  onClickItem,
  onClickItemBox,
  onClickSectionHeaderLink
} = useItemIndexPageAction({
  conditions,
  isFilterOpen,
  searchFromUrl,
  changeSort,
  changeFilters,
  changePage
})
const ERROR_MESSAGE =
  'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
// 検索失敗後のおすすめ表示も既存動作を維持する。
const showEmptyResultContents = computed(
  () => ['success', 'error'].includes(status.value) && items.value.length === 0
)

watch(error, cause => {
  if (cause) show(ERROR_MESSAGE)
})
watch(filterOptionsError, cause => {
  if (cause) show(cause.message || ERROR_MESSAGE)
})

onMounted(async () => {
  // FIXME(item_list_three_columns): ABテスト決着後、削除する
  trackAbTestImpression(props.abTestAssignment)
  await loadOptions()
  await searchFromUrl(location.search)
  window.onpopstate = onPopstate
})
</script>

<style lang="scss" scoped>
.search-result--empty {
  .scene {
    max-width: none;
    width: auto;
  }
}
</style>
